import { useState, useRef, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';
import Vapi from '@vapi-ai/web';
import { bookAppointment, transferToHuman, getOrderStatus, sendEmail, createSupportTicket } from '../utils/functionImplementations';

const functionMap = {
  book_appointment: bookAppointment,
  transfer_to_human: transferToHuman,
  get_order_status: getOrderStatus,
  send_email: sendEmail,
  create_support_ticket: createSupportTicket
};

export const useVapi = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [lastError, setLastError] = useState('');
  const [functionCalls, setFunctionCalls] = useState([]);
  const vapiRef = useRef(null);
  const toast = useToast();

  const handleFunctionCall = async (functionCall) => {
    const { function: func, id } = functionCall;
    
    setFunctionCalls(prev => [...prev, {
      id, name: func.name, arguments: func.arguments,
      timestamp: new Date(), status: 'processing'
    }]);

    try {
      const implementation = functionMap[func.name];
      const result = implementation 
        ? await implementation(func.arguments)
        : { success: false, message: `Unknown function: ${func.name}` };

      setFunctionCalls(prev => prev.map(call => 
        call.id === id ? { ...call, status: result.success ? 'completed' : 'failed', result } : call
      ));

      if (vapiRef.current) {
        vapiRef.current.send({
          type: 'function-call-result',
          functionCallId: id,
          result: result
        });
      }

      toast({
        title: `${func.name} ${result.success ? 'Completed' : 'Failed'}`,
        description: result.message,
        status: result.success ? 'success' : 'error',
        duration: 5000,
        isClosable: true
      });
    } catch (error) {
      const errorResult = { success: false, message: error.message };
      
      setFunctionCalls(prev => prev.map(call => 
        call.id === id ? { ...call, status: 'failed', result: errorResult } : call
      ));

      if (vapiRef.current) {
        vapiRef.current.send({
          type: 'function-call-result',
          functionCallId: id,
          result: errorResult
        });
      }

      toast({
        title: 'Function Call Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  const initializeVapi = (publicKey, useWebsiteContext) => {
    if (!publicKey) return;

    try {
      vapiRef.current = new Vapi(publicKey.trim());
      
      vapiRef.current.on('call-start', () => {
        setIsConnected(true);
        setLastError('');
        setFunctionCalls([]);
        toast({
          title: 'Voice AI Started',
          description: useWebsiteContext ? 'Voice AI with website context is active' : 'Voice conversation is now active',
          status: 'success',
          duration: 3000,
          isClosable: true
        });
      });

      vapiRef.current.on('call-end', () => {
        setIsConnected(false);
        toast({
          title: 'Voice AI Ended',
          description: 'Conversation has ended',
          status: 'info',
          duration: 3000,
          isClosable: true
        });
      });

      vapiRef.current.on('message', (message) => {
        if (message.type === 'transcript' && message.transcriptType === 'final') {
          setTranscript(message.transcript);
          setConversationHistory(prev => [...prev, {
            type: 'user',
            content: message.transcript,
            timestamp: new Date()
          }]);
        }

        if (message.type === 'assistant-message') {
          setAiResponse(message.message);
          setConversationHistory(prev => [...prev, {
            type: 'assistant',
            content: message.message,
            timestamp: new Date()
          }]);
        }

        if (message.type === 'function-call') {
          handleFunctionCall(message);
        }
      });

      vapiRef.current.on('error', (error) => {
        setLastError(`Error: ${error.message || error}`);
        setIsConnected(false);
        toast({
          title: 'Voice AI Error',
          description: error.message || 'An error occurred',
          status: 'error',
          duration: 5000,
          isClosable: true
        });
      });
    } catch (error) {
      setLastError(`Failed to initialize: ${error.message}`);
    }
  };

  const startCall = async (config) => {
    setIsLoading(true);
    setLastError('');

    try {
      await vapiRef.current.start(config);
    } catch (error) {
      const errorMessage = error.message || 'Unknown error occurred';
      setLastError(errorMessage);
      
      toast({
        title: 'Failed to Start Voice AI',
        description: errorMessage,
        status: 'error',
        duration: 8000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  const stopCall = () => {
    if (vapiRef.current) {
      vapiRef.current.stop();
    }
  };

  return {
    isConnected,
    isLoading,
    transcript,
    aiResponse,
    conversationHistory,
    lastError,
    functionCalls,
    initializeVapi,
    startCall,
    stopCall
  };
};